import { useState, useEffect } from "react";
import { useDreams } from "../contexts/DreamsContext";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "../api";
import { Crown, CheckCircle, SpinnerGap, ArrowRight, CreditCard, DeviceMobile, Bank, X, Gift, Copy, ShareNetwork } from "@phosphor-icons/react";

const METHODS = [
  { code: "MB", label: "Multibanco",   icon: Bank,         hint: "Pague em qualquer ATM" },
  { code: "MW", label: "MB Way",       icon: DeviceMobile, hint: "Confirme no telemóvel" },
  { code: "CC", label: "Cartão",       icon: CreditCard,   hint: "Página segura EasyPay" },
];

export default function DreamsSubscribe() {
  const { me, refresh, trialEligible, emTrial, diasRestantes, startTrial } = useDreams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const refToken = params.get("REFER") || params.get("ref");
  const [picked, setPicked] = useState(null);
  const [referral, setReferral] = useState(null);
  const [trialBusy, setTrialBusy] = useState(false);
  const [trialErr, setTrialErr] = useState(null);
  const plans = me?.plans || [];
  const current = me?.subscription;

  const begin = async () => {
    setTrialBusy(true); setTrialErr(null);
    try {
      await startTrial();
      navigate("/dreams");
    } catch (e) {
      setTrialErr(e?.response?.data?.detail || "Não foi possível activar o período gratuito.");
    } finally { setTrialBusy(false); }
  };

  useEffect(() => {
    let cancelled = false;
    if (!current || current.Estado !== "PG") return;
    (async () => {
      try {
        const { data } = await api.get("/referral/me");
        if (!cancelled) setReferral(data);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [current]);

  const cancelAuto = async () => {
    if (!confirm("Cancelar a renovação automática? Manténs acesso até ao fim do período actual.")) return;
    await api.post("/subscribe/cancel", { motivo: "Pedido do utilizador" });
    await refresh();
  };

  return (
    <div className="space-y-10 pb-12">
      <header>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          Escolhe o teu <span className="text-[#6F4F1D]">plano</span>
        </h1>
        <p className="text-[#4B5563] mt-3 max-w-xl">
          Cancela quando quiseres. Sem fidelização. Pagamento via EasyPay (MB / MB Way / Cartão).
        </p>
      </header>

      {refToken && !current && (
        <div className="rounded-2xl border border-[#6F4F1D]/25 bg-[#6F4F1D]/5 p-5 flex items-center gap-3" data-testid="referral-banner">
          <Gift size={22} weight="fill" className="text-[#6F4F1D] shrink-0" />
          <div className="text-sm">
            <p className="font-semibold">Foste convidado por um sonhador!</p>
            <p className="text-xs text-[#4B5563]">Ao subscreveres, o teu amigo ganha +30 dias grátis. Obrigado por fazer crescer a comunidade Dreams.</p>
          </div>
        </div>
      )}

      {referral && (
        <ReferralCard r={referral} />
      )}

      {trialEligible && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-6" data-testid="trial-offer">
          <div className="flex items-start gap-3">
            <Gift size={26} weight="fill" className="text-emerald-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-xl font-bold">Experimenta 3 meses grátis</h2>
              <p className="text-sm text-[#4B5563] mt-1">
                Acesso completo durante 90 dias. Sem cartão, sem compromisso — no fim decides se continuas.
              </p>
              <button
                onClick={begin}
                disabled={trialBusy}
                data-testid="trial-start"
                className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white font-semibold text-sm hover:bg-emerald-500 disabled:opacity-60 transition"
              >
                {trialBusy ? <SpinnerGap size={14} className="animate-spin" /> : <ArrowRight size={13} weight="bold" />}
                {trialBusy ? "A activar…" : "Começar grátis — 3 meses"}
              </button>
              {trialErr && <p className="mt-2 text-xs text-rose-500" data-testid="trial-error">{trialErr}</p>}
            </div>
          </div>
        </div>
      )}

      {current && current.Estado === "PG" && emTrial && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/5 p-5 flex items-center gap-4" data-testid="trial-active-banner">
          <Gift size={26} weight="fill" className="text-emerald-600 shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Estás no período gratuito de 3 meses</p>
            <p className="text-xs text-[#4B5563]">
              {diasRestantes != null ? `Faltam ${diasRestantes} dia${diasRestantes === 1 ? "" : "s"}.` : ""}
              {" "}Acesso completo até {new Date(current.AtivaAte).toLocaleDateString("pt-PT")}.
              {" "}Escolhe um plano abaixo para continuares sem interrupções.
            </p>
          </div>
        </div>
      )}

      {current && current.Estado === "PG" && !emTrial && (
        <div className="rounded-2xl border border-[#6F4F1D]/25 bg-[#6F4F1D]/5 p-5 flex items-center gap-4" data-testid="current-sub-banner">
          <Crown size={28} weight="fill" className="text-[#6F4F1D] shrink-0" />
          <div className="flex-1">
            <p className="font-semibold">Plano {plans.find(p => p.code === current.Plano)?.label || current.Plano} activo</p>
            <p className="text-xs text-[#4B5563]">
              Acesso até {new Date(current.AtivaAte).toLocaleDateString("pt-PT")}.
              {" "}Renovação automática: <strong>{current.AutoRenovar === "S" ? "Activa" : "Cancelada"}</strong>.
            </p>
          </div>
          {current.AutoRenovar === "S" && (
            <button onClick={cancelAuto} data-testid="cancel-auto" className="text-xs text-[#4B5563] hover:text-white underline">
              Cancelar renovação
            </button>
          )}
        </div>
      )}

      <section className="grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="plans-grid">
        {plans.map(p => {
          const isCurrent = current?.Plano === p.code && current?.Estado === "PG" && !emTrial;
          const isBest = p.code === "A";
          return (
            <div
              key={p.code}
              data-testid={`plan-${p.code}`}
              className={`relative rounded-2xl border p-6 flex flex-col transition-all ${
                isBest ? "border-amber-400/50 bg-gradient-to-b from-[#6F4F1D]/10 to-transparent" : "border-[#E6E1D6] bg-white/[0.02]"
              } hover:border-white/30`}
            >
              {isBest && (
                <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-[#6F4F1D] text-[#0F1829] text-[10px] font-bold tracking-wider">
                  POUPA 30%
                </span>
              )}
              <h3 className="text-sm font-semibold uppercase tracking-widest text-[#4B5563]">{p.label}</h3>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-4xl font-bold">{p.preco.toFixed(2).replace(".", ",")}€</span>
              </div>
              <p className="text-[11px] text-[#4B5563] mt-1">
                {p.meses === 1 ? "por mês" : `total · ${p.meses} meses`}
              </p>
              {p.poupanca_pct > 0 && (
                <p className="text-[11px] text-[#6F4F1D] mt-1">Poupa {p.poupanca_pct}%</p>
              )}
              <ul className="mt-5 space-y-2 text-xs flex-1">
                <li className="flex items-start gap-1.5"><CheckCircle size={12} weight="fill" className="text-[#6F4F1D] mt-0.5" /> Sonhos ilimitados</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} weight="fill" className="text-[#6F4F1D] mt-0.5" /> Sugestões IA + plano de acção</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} weight="fill" className="text-[#6F4F1D] mt-0.5" /> Emails motivacionais</li>
                <li className="flex items-start gap-1.5"><CheckCircle size={12} weight="fill" className="text-[#6F4F1D] mt-0.5" /> Cancela quando quiseres</li>
              </ul>
              <button
                onClick={() => setPicked(p)}
                disabled={isCurrent}
                data-testid={`subscribe-${p.code}`}
                className={`mt-5 w-full px-4 py-2.5 rounded-full text-sm font-semibold flex items-center justify-center gap-1.5 transition-all ${
                  isCurrent
                    ? "bg-white text-white/60 cursor-default"
                    : isBest
                      ? "bg-gradient-to-r from-[#6F4F1D] to-fuchsia-500 text-[#0F1829] hover:shadow-[0_0_30px_rgba(245,158,11,0.4)]"
                      : "bg-white text-[#0F1829] hover:bg-amber-100"
                }`}
              >
                {isCurrent ? "Plano actual" : <>Subscrever <ArrowRight size={13} weight="bold" /></>}
              </button>
            </div>
          );
        })}
      </section>

      {picked && <CheckoutModal plan={picked} refToken={refToken} onClose={() => setPicked(null)} onPaid={() => { setPicked(null); refresh(); navigate("/dreams"); }} />}
    </div>
  );
}

function ReferralCard({ r }) {
  const url = `${window.location.origin}/dreams/landing?REFER=${r.refer_id}`;
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };
  return (
    <div className="rounded-2xl border border-fuchsia-400/30 bg-gradient-to-br from-violet-900/30 to-fuchsia-900/20 p-6" data-testid="referral-card">
      <div className="flex items-center gap-2 mb-3">
        <Gift size={22} weight="fill" className="text-[#6F4F1D]" />
        <h2 className="text-xl font-bold">Convida amigos · ganha meses grátis</h2>
      </div>
      <p className="text-sm text-[#0F1829]">Por cada amigo que subscreva via o teu link, recebes <strong className="text-[#6F4F1D]">+30 dias grátis</strong>.</p>
      <div className="mt-4 flex gap-2">
        <input value={url} readOnly data-testid="referral-link"
               className="flex-1 bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-sm" />
        <button onClick={copy} data-testid="referral-copy"
                className={`px-3 py-2 rounded-lg text-sm font-semibold inline-flex items-center gap-1.5 ${
                  copied ? "bg-emerald-400 text-[#0F1829]" : "bg-[#6F4F1D] text-[#0F1829]"
                }`}>
          {copied ? <><CheckCircle size={13} weight="bold" /> Copiado</> : <><Copy size={13} weight="bold" /> Copiar</>}
        </button>
      </div>
      <div className="mt-4 flex flex-wrap gap-4 text-xs text-[#4B5563]">
        <span><strong className="text-[#6F4F1D]">{r.convertidos || 0}</strong> conversões · <strong className="text-[#6F4F1D]">{r.dias_oferecidos || 0}</strong> dias ganhos</span>
        {r.pendentes > 0 && <span>{r.pendentes} convite{r.pendentes === 1 ? "" : "s"} pendente{r.pendentes === 1 ? "" : "s"} de pagamento</span>}
        <a href={`https://wa.me/?text=${encodeURIComponent(`Estou no IHTP Dreams a transformar a vida! Junta-te a mim: ${url}`)}`}
           target="_blank" rel="noopener noreferrer"
           className="text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1">
          <ShareNetwork size={11} /> Partilhar no WhatsApp
        </a>
      </div>
    </div>
  );
}

function CheckoutModal({ plan, refToken, onClose, onPaid }) {
  const [step, setStep] = useState("choose"); // choose | mb | mw | cc | error
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [phone, setPhone] = useState("");
  const [err, setErr] = useState(null);

  const submit = async (metodo, payload = {}) => {
    setBusy(true); setErr(null);
    try {
      const body = { plano: plan.code, metodo, ...payload };
      if (refToken) body.REFER = refToken;
      const { data } = await api.post("/subscribe", body);
      if (data?.pagamento?.success) {
        setResult(data.pagamento);
        setStep(metodo.toLowerCase());
        if (metodo === "CC" && data.pagamento.url) {
          window.open(data.pagamento.url, "_blank", "noopener");
        }
      } else {
        setErr(data?.pagamento?.message || "Erro a criar pagamento.");
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} data-testid="checkout-modal"
           className="bg-[#F5F2EB] border border-[#E6E1D6] rounded-2xl w-full max-w-md">
        <header className="border-b border-[#E6E1D6] px-6 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold">Plano {plan.label}</h2>
            <p className="text-xs text-[#4B5563]">{plan.preco.toFixed(2).replace(".", ",")}€ · {plan.meses}m</p>
          </div>
          <button onClick={onClose} className="text-[#978D78] hover:text-white" data-testid="checkout-close"><X size={20} /></button>
        </header>
        <div className="p-6 space-y-3">
          {step === "choose" && (
            <>
              {METHODS.map(m => (
                <button key={m.code} onClick={() => m.code === "MW" ? setStep("ask-phone") : submit(m.code)}
                        disabled={busy} data-testid={`pay-${m.code}`}
                        className="w-full flex items-center gap-3 p-4 rounded-xl border border-[#E6E1D6] bg-white/[0.02] hover:bg-white hover:border-[#6F4F1D]/25 text-left">
                  <m.icon size={22} className="text-[#6F4F1D]" weight="fill" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm">{m.label}</p>
                    <p className="text-[11px] text-[#978D78]">{m.hint}</p>
                  </div>
                  {busy ? <SpinnerGap size={16} className="animate-spin" /> : <ArrowRight size={14} />}
                </button>
              ))}
            </>
          )}
          {step === "ask-phone" && (
            <>
              <p className="text-sm text-[#4B5563]">Indica o teu telemóvel MB Way (9 dígitos, começa por 91/92/93/96):</p>
              <input data-testid="mbw-phone" autoFocus inputMode="numeric"
                     value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                     placeholder="912345678"
                     className="w-full bg-white border border-[#E6E1D6] rounded-lg px-3 py-2 text-white" />
              <button onClick={() => submit("MW", { telemovel: phone })} disabled={busy || phone.length !== 9}
                      data-testid="mbw-submit"
                      className="w-full mt-2 px-4 py-2.5 rounded-full bg-[#6F4F1D] text-[#0F1829] font-semibold text-sm disabled:opacity-40 inline-flex items-center justify-center gap-1.5">
                {busy ? <SpinnerGap size={14} className="animate-spin" /> : <>Pedir pagamento <ArrowRight size={13} weight="bold" /></>}
              </button>
            </>
          )}
          {step === "mb" && result && (
            <div className="text-center" data-testid="mb-result">
              <p className="text-sm text-[#4B5563]">Pague em qualquer ATM com:</p>
              <div className="mt-4 grid gap-2">
                <div className="rounded-lg bg-white border border-[#E6E1D6] p-3">
                  <p className="text-[10px] uppercase tracking-widest text-[#6F4F1D]">Entidade</p>
                  <p className="text-2xl font-bold tracking-wider mt-1">{result.entidade}</p>
                </div>
                <div className="rounded-lg bg-white border border-[#E6E1D6] p-3">
                  <p className="text-[10px] uppercase tracking-widest text-[#6F4F1D]">Referência</p>
                  <p className="text-2xl font-bold tracking-wider mt-1">{result.referencia}</p>
                </div>
                <div className="rounded-lg bg-[#6F4F1D]/10 border border-[#6F4F1D]/25 p-3">
                  <p className="text-[10px] uppercase tracking-widest text-[#6F4F1D]">Valor</p>
                  <p className="text-2xl font-bold mt-1">{result.valor.toFixed(2).replace(".", ",")}€</p>
                </div>
              </div>
              <p className="text-[11px] text-[#978D78] mt-3">Após o pagamento, a subscrição activa-se automaticamente.</p>
            </div>
          )}
          {step === "mw" && result && (
            <div className="text-center" data-testid="mw-result">
              <DeviceMobile size={42} weight="fill" className="text-[#6F4F1D] mx-auto animate-pulse" />
              <p className="text-base font-semibold mt-3">A aguardar confirmação no MB Way</p>
              <p className="text-sm text-[#4B5563] mt-1">Verifica a app no telemóvel {result.telemovel}.</p>
              <p className="text-[11px] text-[#978D78] mt-3">A subscrição activa-se automaticamente após o pagamento.</p>
            </div>
          )}
          {step === "cc" && result && (
            <div className="text-center" data-testid="cc-result">
              <CreditCard size={42} weight="fill" className="text-[#6F4F1D] mx-auto" />
              <p className="text-base font-semibold mt-3">Abrimos a página de pagamento numa nova aba.</p>
              <p className="text-sm text-[#4B5563] mt-1">Se não abriu:</p>
              <a href={result.url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-1.5 mt-3 px-4 py-2 rounded-full bg-[#6F4F1D] text-[#0F1829] text-sm font-semibold">
                Abrir pagamento <ArrowRight size={13} weight="bold" />
              </a>
            </div>
          )}
          {err && <p className="text-xs text-rose-300 text-center mt-2" data-testid="checkout-err">{err}</p>}
          {(step === "mb" || step === "mw" || step === "cc") && (
            <button onClick={onPaid} data-testid="checkout-done"
                    className="w-full mt-4 text-xs text-[#978D78] hover:text-white underline">
              Já paguei / Fechar
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
